import React, { useEffect, useMemo, useState, useRef } from 'react';
import axios from 'axios';
import { useLocation, useNavigate } from 'react-router-dom';

const OrderSelected = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const selectedAddressFromState = location.state?.selectedAddress;
  const selectedCouponIdFromState = location.state?.selectedCouponId;

  const query = new URLSearchParams(location.search);

  const cartIdsFromState = location.state?.cartIds || [];
  const cartIdsFromQuery = (query.get('cart_ids') || '')
    .split(',')
    .map(v => Number(v.trim()))
    .filter(v => !Number.isNaN(v) && v > 0);

  const cartIds = cartIdsFromState.length > 0 ? cartIdsFromState : cartIdsFromQuery;

  const [selectedAddress, setSelectedAddress] = useState(null);

  const reserveYnQ = query.get('reserve_yn') || 'N';
  const pickupDateQ = query.get('pickup_date') || '';
  const pickupTimeQ = query.get('pickup_time') || '';
  const pickupDatetimeQ = query.get('pickup_datetime') || '';

  const [cartItems, setCartItems] = useState([]);

  const [buyer, setBuyer] = useState(() => ({
    name: '',
    phone: ''
  }));

  const [editingBuyer, setEditingBuyer] = useState(false);
  const phoneInputRef = useRef(null);

  const [price, setPrice] = useState({ total: 0, delivery: 0, result: 0 });

  const orderTypeFromState = location.state?.orderType;
  const [orderType, setOrderType] = useState(
    orderTypeFromState || 'pickup'
  );
  const [reserveYn, setReserveYn] = useState('N');
  const [pickupDate, setPickupDate] = useState('');
  const [pickupTime, setPickupTime] = useState('');
  const [pickupDatetime, setPickupDatetime] = useState('');
  const [showMiniInfo, setShowMiniInfo] = useState(false);
  const [deliveryEnabled, setDeliveryEnabled] = useState(0);

  const [coupons, setCoupons] = useState([]);
  const [couponLoading, setCouponLoading] = useState(true);

  const [selectedCoupon, setSelectedCoupon] = useState('');

  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  const selectedCouponObj = useMemo(() => {
    return coupons.find(
      c => String(c.member_coupon_id) === selectedCoupon
    );
  }, [coupons, selectedCoupon]);

  const couponDiscount = useMemo(() => {
    if (!selectedCouponObj) return 0;
  
    const subtotal = Number(price.total || 0);
  
    if (subtotal < selectedCouponObj.min_order_price) {
      return 0;
    }
  
    if (selectedCouponObj.discount_type === 'amount') {
      return selectedCouponObj.discount_value;
    }
  
    if (selectedCouponObj.discount_type === 'percent') {
      return Math.floor(
        subtotal * (selectedCouponObj.discount_value / 100)
      );
    }
  
    return 0;
  }, [selectedCouponObj, price.total]);

  const productDiscount = useMemo(() => {
    return cartItems.reduce((sum, item) => {
      const originPrice = Number(item.price || 0);
      const salePrice = Number(item.display_price || 0);
      const qty = Number(item.quantity || 0);
  
      if (salePrice >= originPrice) return sum;
  
      return sum + ((originPrice - salePrice) * qty);
    }, 0);
  }, [cartItems]);

  const optionTotalPrice = useMemo(() => {
    return cartItems.reduce((sum, item) => {
      const optionTotal = Number(item.option_total || 0);
      const qty = Number(item.quantity || 0);
  
      return sum + (optionTotal * qty);
    }, 0);
  }, [cartItems]);

  useEffect(() => {
    if (!window.daum) {
      const script = document.createElement("script");
      script.src = "//t1.daumcdn.net/mapjsapi/bundle/postcode/prod/postcode.v2.js";
      script.async = true;
      document.body.appendChild(script);
    }

    if (!cartIds || cartIds.length === 0) {
      alert('선택된 상품이 없습니다.');
      navigate('/cart');
      return;
    }

    axios.get('/api/order/selected/preview', {
      params: {
        cart_ids: cartIds.join(','),
        reserve_yn: reserveYnQ,
        pickup_date: pickupDateQ,
        pickup_time: pickupTimeQ,
        pickup_datetime: pickupDatetimeQ
      }
    })
      .then(res => {
        const d = res.data;
        setCartItems(d.cart_items || []);

        if (selectedAddressFromState) {
          setSelectedAddress(selectedAddressFromState);
        } else {
          const def =
            d.default_address ||
            d.all_addresses?.find(
              a => Number(a.is_default) === 1
            ) ||
            d.all_addresses?.[0] ||
            null;
        
          setSelectedAddress(def);
        }

        setDeliveryEnabled(Number(d.delivery_enabled || 0));

        setBuyer(prev => ({
          name: d.member_info?.nickname || d.member_info?.name || '',
          phone: prev.phone || d.member_info?.phone || ''
        }));

        setPrice({
          total: d.total_price || 0,
          delivery: d.delivery_cost || 0,
          result: d.result_price || 0
        });

        setReserveYn(d.reserve_yn_q || 'N');
        setPickupDate(d.pickup_date_q || '');
        setPickupTime(d.pickup_time_q || '');
        setPickupDatetime(d.pickup_datetime_q || '');
      })
      .catch(err => {
        if (err.response && err.response.data && err.response.data.message) {
          alert(err.response.data.message);
        } else {
          alert("데이터를 불러올 수 없습니다.");
        }
        navigate(-1);
      });
  }, [cartIds, navigate, reserveYnQ, pickupDateQ, pickupTimeQ, pickupDatetimeQ]);

  useEffect(() => {
    if (phoneInputRef.current && editingBuyer) {
      phoneInputRef.current.focus();
    }
  }, [editingBuyer]);

  useEffect(() => {
      if (selectedCouponIdFromState) {
          setSelectedCoupon(String(selectedCouponIdFromState));
      } else {
          setSelectedCoupon('');
      }
  }, [selectedCouponIdFromState]);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    axios.get('/api/coupon', {
      withCredentials: true
    })
    .then(res => {
      if (res.data.success || res.data.coupons) {
        setCoupons(res.data.coupons || []);
  
        if (selectedCouponIdFromState) {
          setSelectedCoupon(String(selectedCouponIdFromState));
        }
      }
    })
    .catch(err => {
      console.error(err);
    })
    .finally(() => {
      setCouponLoading(false);
    });
  }, [selectedCouponIdFromState]);

  const displayDeliveryCost = useMemo(() => {
    if (orderType !== 'delivery') return 0;
    return Number(price.total) >= 30000 ? 0 : 3000;
  }, [orderType, price.total]);

  const displayResultPrice = useMemo(() => {
    return Math.max(
      0,
      Number(price.total || 0)
      + Number(displayDeliveryCost || 0)
      - Number(couponDiscount || 0)
    );
  }, [price.total, displayDeliveryCost, couponDiscount]);

  const handlePickupClick = () => {
    setOrderType('pickup');
  };

  const handleDeliveryClick = () => {
    setOrderType('delivery');
  };

  const handleReserveToggle = (e) => {
    const checked = e.target.checked;

    if (checked) {
      setReserveYn('Y');

      const params = new URLSearchParams();
      params.set('cart_ids', cartIds.join(','));
      params.set('reserve_yn', 'Y');
      params.set('selected_coupon_id', selectedCoupon);

      if (pickupDate) params.set('pickup_date', pickupDate);
      if (pickupTime) params.set('pickup_time', pickupTime);
      if (pickupDatetime) params.set('pickup_datetime', pickupDatetime);

      navigate(`/pickup/calendar?${params.toString()}`);
    } else {
      setReserveYn('N');
      setPickupDate('');
      setPickupTime('');
      setPickupDatetime('');

      navigate(`/order/selected?cart_ids=${encodeURIComponent(cartIds.join(','))}`, {
        replace: true,
        state: {
          cartIds,
          selectedCouponId: selectedCoupon
        }
      });
    }
  };

  const formatPhoneNumber = (value) => {
    const onlyNums = value.replace(/\D/g, '');
  
    if (onlyNums.length < 4) return onlyNums;
    if (onlyNums.length < 8) {
      return onlyNums.replace(/(\d{3})(\d+)/, '$1-$2');
    }
    return onlyNums.replace(/(\d{3})(\d{4})(\d+)/, '$1-$2-$3');
  };

  const handleBuyerButton = async () => {

    // 처음 클릭은 입력창만 열기
    if (!editingBuyer) {
      setEditingBuyer(true);
      return;
    }
  
    if (!buyer.phone.trim()) {
      alert('전화번호를 입력해주세요.');
      phoneInputRef.current?.focus();
      return;
    }
  
    try {
      await axios.post('/api/order/buyer', {
        buyer: {
          phone: buyer.phone.replace(/\D/g, '')
        }
      });
  
      setEditingBuyer(false);
  
    } catch (err) {
      alert(err.response?.data?.message || '저장에 실패했습니다.');
    }
  };

  const formatPhoneDisplay = (phone = '') => {
    let nums = phone.replace(/\D/g, '');
  
    // 010 없이 들어온 짧은 번호는 앞에 010 보정
    if (nums.length <= 9) {
      nums = '010' + nums;
    }
  
    // 11자리 기준
    if (nums.length >= 11) {
      nums = nums.slice(0, 11);
      return nums.replace(/(\d{3})(\d{4})(\d{4})/, '$1-$2-$3');
    }
  
    return nums;
  };

  const goToPayPage = async () => {
    if (!buyer.name || !buyer.phone) {
      alert("주문자 정보를 입력해주세요.");

      setEditingBuyer(true);
    
      setTimeout(() => {
        phoneInputRef.current?.focus();
      }, 100);

      return;
    }

    if (orderType === 'delivery') {
      if (!selectedAddress) {
        alert('배송지를 선택해주세요.');
        return;
      }
    }

    if (orderType === 'pickup' && reserveYn === 'Y') {
      const hasPickupDate = pickupDate.trim() !== '';
      const hasPickupTime = pickupTime.trim() !== '';
      const hasPickupDatetime = pickupDatetime.trim() !== '';

      if (!hasPickupDate || !hasPickupTime || !hasPickupDatetime) {
        alert('예약픽업을 선택하셨으면 날짜와 시간을 먼저 선택해 주세요.');
        return;
      }
    }

    try {
      const res = await axios.post('/api/order/selected/save', {
        buyer_name: buyer.name,
        buyer_phone: buyer.phone,
        buyer_email: buyer.email,

        member_coupon_id: Number(selectedCoupon) || 0,

        cart_ids: cartIds.join(','),
        order_type: orderType,
        reserve_yn: reserveYn,
        pickup_date: pickupDate,
        pickup_time: pickupTime,
        pickup_datetime: pickupDatetime,
        receiver_name:
          orderType === 'delivery'
            ? selectedAddress.receiver_name
            : buyer.name,

        receiver_phone:
          orderType === 'delivery'
            ? selectedAddress.receiver_phone
            : buyer.phone,

        address:
          orderType === 'delivery'
            ? selectedAddress.address
            : '매장 픽업',

        detail:
          orderType === 'delivery'
            ? selectedAddress.detail_address
            : ''
      });

      if (res.data.success) {

        navigate('/pay/selected', {
          state: {
            orderId: res.data.order_id,
            amount: res.data.amount,
            orderName: cartItems.length > 1 ? `${cartItems[0].name} 외 ${cartItems.length - 1}건` : cartItems[0].name,
            buyerName: buyer.name,
            cartIds: cartIds,
            reserve_yn: reserveYn,
            pickup_date: pickupDate,
            pickup_time: pickupTime,
            pickup_datetime: pickupDatetime
          }
        });
      }
    } catch (err) {
      if (err.response?.data?.message) {
        alert(err.response.data.message);
      } else {
        alert("주문 처리 중 오류 발생");
      }
    }
  };

  return (
    <div
      style={{
        ...styles.bodyWrapper,
        paddingBottom: isMobile ? '90px' : '40px'
      }}
    >
      <div style={styles.container}>
        <h2 style={styles.pageTitle}>결제하기</h2>

        <div style={styles.orderDetailCard}>
          <div style={styles.orderHeader}><strong>주문내역</strong></div>
          <div style={{paddingLeft: '2px', paddingRight: '2px'}}>

          {cartItems.map((item, idx) => {
            const qty = Number(item.quantity || 0);
            const basePrice = Number(item.display_price || 0);
            const originPrice = Number(item.price || 0);
            const optionTotal = Number(item.option_total || 0);

            const unitPrice = basePrice + optionTotal;
            const originUnitPrice = originPrice + optionTotal;

            return (
              <div key={idx} style={styles.itemRow}>
                <img
                  src={item.image_path?.startsWith('http') ? item.image_path : `/static/${item.image_path}`}
                  alt="상품 이미지"
                  style={styles.itemImg}
                />

                <div style={{ flexGrow: 1 }}>
                  <div style={styles.itemName}>{item.name}</div>

                  {item.options?.length > 0 && (
                    <div style={styles.optionBox}>
                      {item.options.map((opt, optIdx) => (
                        <div key={optIdx} style={styles.optionText}>
                          {opt.option_group_name}: {opt.option_item_name}
                          {Number(opt.option_price || 0) > 0 &&
                            ` (+${Number(opt.option_price || 0).toLocaleString()}원)`
                          }
                        </div>
                      ))}
                    </div>
                  )}

                  <div style={styles.itemSub}>
                    수량: {item.quantity}
                  </div>

                  {Number(item.price) !== Number(item.display_price) && (
                    <div
                      style={{
                        textDecoration: 'line-through',
                        color: '#999',
                        fontSize: '12px',
                        marginTop: '3px'
                      }}
                    >
                      {Number(originUnitPrice * qty).toLocaleString()}원
                    </div>
                  )}

                </div>

                <div
                  style={{
                    ...styles.itemPrice,
                    marginTop: item.options?.length > 0 ? '0px' : '10px'
                  }}
                >
                  {Number(unitPrice * qty).toLocaleString()}원
                </div>
              </div>
            );
          })}

          </div>
        </div>

        <div
          style={{
            margin: '0 15px',
            borderBottom: '1px solid #e5e5e5'
          }}
        />

        {/* 쿠폰 */}
        <div style={styles.orderDetailCard}>
          <div style={styles.orderHeader}>
            <strong>쿠폰</strong>
          </div>

          <div
            onClick={() =>
              navigate('/coupon', {
                state: {
                  returnTo: '/order/selected',
                  cartIds,
                  orderType,
                  selectedCoupon: selectedCouponObj,
                  subtotal: Number(price.total || 0),

                  reserveYn,
                  pickupDate,
                  pickupTime,
                  pickupDatetime
                }
              })
            }
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '0px',
              cursor: 'pointer'
            }}
          >
            <div>
              <div
                style={{
                  color: '#888',
                  fontSize: '14px',
                  marginTop: '-10px'
                }}
              >
                {selectedCouponObj
                  ? `${selectedCouponObj.coupon_name} - ${couponDiscount.toLocaleString()}원 할인 적용됨`
                  : '사용 가능한 쿠폰 선택'}
              </div>
            </div>

            <div style={{ color: '#888', fontSize: '22px', marginTop: '-12px' }}>
              ›
            </div>
          </div>
        </div>

        <div
          style={{
            margin: '0 15px',
            borderBottom: '1px solid #e5e5e5'
          }}
        />

        <div style={styles.orderDetailCard}>
          <div style={styles.orderHeader}>
            <strong>주문자 정보</strong>

            <button
              type="button"
              onClick={handleBuyerButton}
              style={{
                border: '1px solid #ddd',
                background: '#fff',
                borderRadius: '20px',
                padding: '2px 14px',
                cursor: 'pointer',
                fontSize: '16px'
              }}
            >
              {editingBuyer ? '저장' : '변경'}
            </button>
          </div>

          <div style={{ padding: '0px' }}>

            {/* 이름은 항상 표시 */}
            <div
              style={{
                fontSize: '18px',
                fontWeight: '700',
                marginBottom: '4px'
              }}
            >
              {buyer.name}
            </div>

            {/* 전화번호 */}
            {editingBuyer ? (
              <input
                ref={phoneInputRef}
                autoFocus
                value={buyer.phone}
                maxLength={13}
                onChange={(e) =>
                  setBuyer({
                    ...buyer,
                    phone: formatPhoneNumber(e.target.value)
                  })
                }
                placeholder="전화번호 입력"
                style={{
                  width: '100%',
                  padding: '10px',
                  border: '2px solid #0073e9',
                  borderRadius: '6px',
                  outline: 'none',
                  backgroundColor: '#fff',
                  color: '#333',
                  fontSize: '16px'
                }}
              />
            ) : buyer.phone ? (
              <div
                style={{
                  color: '#555'
                }}
              >
                {formatPhoneDisplay(buyer.phone)}
              </div>
            ) : (
              <div
                onClick={() => setEditingBuyer(true)}
                style={{
                  color: '#0d6efd',
                  fontWeight: '700',
                  cursor: 'pointer'
                }}
              >
                + 전화번호 입력
              </div>
            )}

            
              <div
                style={{
                  marginTop: '10px',
                  fontSize: '13px',
                  color: '#888'
                }}
              >
                주문 관련 안내를 받을 연락처입니다.
              </div>
           
          </div>
        </div>

        <div
          style={{
            margin: '0 15px',
            borderBottom: '1px solid #e5e5e5'
          }}
        />

        {deliveryEnabled===1&&(

          <div style={styles.orderDetailCard}>

              <div style={styles.orderHeader}>
                  <strong>이용방법</strong>
              </div>

              <div style={styles.orderTypeToggle}>

                  <button
                      type="button"
                      onClick={handlePickupClick}
                      style={
                          orderType==="pickup"
                          ?styles.orderTypeBtnActive
                          :styles.orderTypeBtn
                      }
                  >
                      매장 픽업
                  </button>

                  <button
                      type="button"
                      onClick={handleDeliveryClick}
                      style={
                          orderType==="delivery"
                          ?styles.orderTypeBtnActive
                          :styles.orderTypeBtn
                      }
                  >
                      배달 수령
                  </button>

              </div>

          </div>

        )}

        <div
          style={{
            margin: '0 15px',
            borderBottom: '1px solid #e5e5e5'
          }}
        />

        {deliveryEnabled === 1 &&
        orderType === 'delivery' && (

        <div style={styles.orderDetailCard}>
          <div style={styles.orderHeader}>
            <strong>배송정보</strong>

            <button
              type="button"
              onClick={() =>
                navigate('/address/list', {
                  state: {
                    returnTo: '/order/selected',
                    cartIds,
                    orderType,
                    selectedCouponId: selectedCoupon
                  }
                })
              }
              style={{
                border: '1px solid #ddd',
                background: '#fff',
                borderRadius: '20px',
                padding: '2px 14px',
                cursor: 'pointer',
                fontSize: '16px'
              }}
            >
              변경
            </button>
          </div>

          <div style={{ padding: '0px' }}>
            {selectedAddress ? (
              <>
                <div
                  style={{
                    fontSize: '18px',
                    fontWeight: '700',
                    marginBottom: '10px'
                  }}
                >
                  {selectedAddress.receiver_name}
                </div>

                <div
                  style={{
                    marginBottom: '10px',
                    lineHeight: '1.5'
                  }}
                >
                  {selectedAddress.address}
                  {' '}
                  {selectedAddress.detail_address}
                </div>

                <div
                  style={{
                    marginBottom: '10px',
                    color: '#555'
                  }}
                >
                  {formatPhoneDisplay(selectedAddress.receiver_phone)}
                </div>
              </>
            ) : (
              <>
                <div
                  style={{
                    textAlign: 'center',
                    padding: '20px',
                    color: '#999'
                  }}
                >
                  등록된 배송지가 없습니다.
                </div>

                <div
                  style={{
                    textAlign: 'center',
                    marginTop: '16px'
                  }}
                >
                  <span
                    onClick={() =>
                      navigate('/address/list', {
                        state: {
                          returnTo: '/order/selected',
                          cartIds,
                          orderType,
                          selectedCouponId: selectedCoupon
                        }
                      })
                    }
                    style={{
                      fontWeight: '700',
                      color: '#0073e9',
                      cursor: 'pointer'
                    }}
                  >
                    + 배송지 등록
                  </span>
                </div>
              </>
            )}

          </div>
        </div>
        )}

        {orderType === 'pickup' && (
          <div style={styles.orderDetailCard}>
            <div style={{ ...styles.orderHeader, position: 'relative' }}>
              <div>
                <strong>픽업시간</strong>
                <span
                  onClick={() => setShowMiniInfo(!showMiniInfo)}
                  style={styles.infoIcon}
                >
                  i
                </span>
              </div>

              {showMiniInfo && (
                <div style={styles.miniInfoBox}>
                  <strong style={{ color: '#222' }}>매장 픽업 안내</strong>
                  <hr style={{ margin: '8px 0' }} />
                  • 매장픽업은 배송료없음<br />
                  • 체크박스해제(바로픽업)는 주문즉시 준비해드립니다.<br />
                  • 체크박스선택(예약픽업)는 예약한 시간까지 준비해드립니다.
                  <hr style={{ margin: '8px 0' }} />
                </div>
              )}
            </div>

            <div style={{ paddingLeft: '2px', paddingRight: '2px' }}>

              <label style={styles.reserveLabel}>
                <input
                  type="checkbox"
                  checked={reserveYn === 'Y' && !!pickupDatetime}
                  onChange={handleReserveToggle}
                  style={styles.reserveCheckbox}
                />
                예약하고 찾으러 갈게요!
              </label>

              {!!pickupDatetime && (
                <div style={styles.pickupSelectedBox}>
                  {String(pickupDatetime).slice(0, 16).replace('T', ' ')}까지 방문할게요!
                </div>
              )}

            </div>
          </div>
        )}


        {/* 결제금액 */}
        <div
          style={{
            ...styles.orderDetailCard,
            padding: 0
          }}
        >

        <div style={styles.priceSummary}>

            <div style={styles.priceRow}>
                <span>상품 금액</span>
                <span>{(price.total + productDiscount - optionTotalPrice).toLocaleString()}원</span>
            </div>

            {optionTotalPrice > 0 && (
              <div style={styles.priceRow}>
                  <span>옵션 금액</span>
                  <span>{optionTotalPrice.toLocaleString()}원</span>
              </div>
            )}

            <div style={styles.priceRow}>
                <span>상품 할인</span>
                <span>-{productDiscount.toLocaleString()}원</span>
            </div>

            <div style={styles.priceRow}>
                <span>쿠폰 할인</span>
                <span>-{couponDiscount.toLocaleString()}원</span>
            </div>

            <div style={styles.priceRow}>
                <span>배송료</span>
                <span>{displayDeliveryCost.toLocaleString()}원</span>
            </div>

            <hr
              style={{
                margin: '12px 0',
                border: '0',
                borderTop: '1px solid #eee'
              }}
            />

            <div
                style={{
                    ...styles.priceRow,
                    fontWeight:'700',
                    fontSize:'18px'
                }}
            >
                <span>최종 결제금액</span>
                <span style={{color:'#0073e9'}}>
                    {displayResultPrice.toLocaleString()}원
                </span>
            </div>
          </div>
        </div>

        {isMobile ? (
          <div style={styles.bottomFixed}>
            <button
              style={styles.btnBuyMobile}
              onClick={goToPayPage}
              >
              {orderType === 'delivery'
                ? '배달주문 결제하기'
                : pickupDatetime
                  ? '예약픽업 결제하기'
                  : '바로픽업 결제하기'}
            </button>
          </div>
        ) : (
          <div style={styles.bottomAction}>
            <button
              style={styles.submitBtn}
              onClick={goToPayPage}
            >
              {orderType === 'delivery'
                ? '배달주문 결제하기'
                : pickupDatetime
                  ? '예약픽업 결제하기'
                  : '바로픽업 결제하기'}
            </button>
          </div>
        )}

      </div>
    </div>
  );
};

const styles = {
  bodyWrapper: { backgroundColor: '#fff', minHeight: '100vh', paddingBottom: '40px' },
  container: {
    maxWidth: '600px',
    margin: '0 auto',
    padding: '0 6px',
    boxSizing: 'border-box',
    fontFamily: "'Apple SD Gothic Neo', 'Noto Sans KR', sans-serif"
  },
  pageTitle: { fontSize: '1.5rem', fontWeight: 'bold', padding: '24px 14px 0', margin: '0' },
  orderDetailCard: { background: '#fff', marginTop: '0px', borderRadius: '1px', padding: '15px' },
  orderHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '0px solid #eee', paddingBottom: '10px', marginBottom: '15px', fontSize: '18px', padding: '0px' },
  orderTypeToggle: { display: 'flex', margin: '20px 0px 10px 0px', gap: '10px', padding: '0px 0px' },
  orderTypeBtn: { flex: 1, padding: '12px', border: '1px solid #ddd', background: '#fff', cursor: 'pointer', borderRadius: '6px', fontWeight: 'bold' },
  orderTypeBtnActive: { flex: 1, padding: '12px', border: '2px solid #0d6efd', background: '#fff', cursor: 'pointer', borderRadius: '6px', fontWeight: 'bold' },
  cardBody: { padding: '0' },
  itemRow: { display: 'flex', alignItems: 'center', gap: '15px', padding: '6px 0px' },
  itemImg: { width: '60px', height: '60px', objectFit: 'cover', border: '0px solid #ddd', borderRadius: '4px' },
  itemName: { fontWeight: 'bold', fontSize: '16px' },
  itemSub: { color: '#6c757d', fontSize: '12px' },
  optionBox: {
    marginTop: '4px'
  },
  
  optionText: {
    color: '#777',
    fontSize: '12px',
    lineHeight: '1.4'
  },
  itemPrice: { textAlign: 'right', fontWeight: '600', fontSize: '16px', alignSelf: 'flex-start', marginTop: '0px' },
  priceSummary: {
    margin: '0',
    marginLeft: '-6px',
    marginRight: '-6px',
    padding: '20px',
    backgroundColor: '#f8f8f8',
    fontSize: '14px'
  },
  priceRow: { display: 'flex', justifyContent: 'space-between' },
  tabMenu: { justifyContent: 'center', display: 'flex', listStyle: 'none', padding: '0', margin: '0 2px 20px 2px', borderTop: '2px solid #e9ecef', borderBottom: '2px solid #e9ecef', fontWeight: '600' },
  tab: { padding: '15px 20px 12px', cursor: 'pointer', color: '#6c757d', fontSize: '11px', lineHeight: '1.2', borderBottom: '3px solid transparent', transition: 'all 0.3s ease', flex: 1, textAlign: 'center' },
  tabActive: { padding: '15px 20px 12px', cursor: 'pointer', color: '#0073e9', fontSize: '11px', lineHeight: '1.2', borderBottom: '3px solid #0073e9', transition: 'all 0.3s ease', fontWeight: '700', flex: 1, textAlign: 'center' },
  floatingGroup: { position: 'relative', marginBottom: '1rem' },
  input: { width: '100%', padding: '1.5rem 0.75rem 0.5rem', fontSize: '1rem', border: '1px solid #dee2e6', borderRadius: '0.375rem', boxSizing: 'border-box', outline: 'none' },
  floatingLabel: { position: 'absolute', top: '0', left: '0', padding: '1rem 0.75rem', fontSize: '0.85rem', color: '#6c757d', pointerEvents: 'none', transform: 'scale(.85) translateY(-0.5rem) translateX(0.15rem)', transition: 'all 0.1s' },
  checkRow: { display: 'flex', alignItems: 'center', margin: '10px 0' },
  checkbox: { marginRight: '10px', width: '16px', height: '16px' },
  smallLabel: { fontSize: '0.875rem', color: '#444' },
  addrListItem: { display: 'flex', padding: '12px 0', borderBottom: '1px solid #e0e0e0', cursor: 'pointer', alignItems: 'flex-start' },
  addBtn: { padding: '10px 14px', borderRadius: '4px', cursor: 'pointer' },
  submitBtn: { width: '100%', padding: '16px', backgroundColor: '#0073e9', color: '#fff', border: 'none', borderRadius: '6px', fontWeight: 'bold', fontSize: '1rem', cursor: 'pointer', marginTop: '10px' },
  infoIcon: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '18px',
    height: '18px',
    borderRadius: '50%',
    border: '1px solid #0d6efd',
    color: '#0d6efd',
    fontSize: '12px',
    marginLeft: '8px',
    cursor: 'pointer',
    fontWeight: 'bold'
  },
  miniInfoBox: {
    display: 'block',
    position: 'absolute',
    top: '28px',
    left: '0',
    zIndex: 1055,
    width: '250px',
    fontSize: '0.75rem',
    color: '#6c757d',
    border: '1px solid #ddd',
    borderRadius: '8px',
    padding: '10px',
    background: '#f8f9fa',
    boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
  },
  reserveLabel: { display: 'inline-flex', alignItems: 'center', gap: '8px', marginTop: '12px', marginBottom: '12px', fontSize: '16px', width: 'auto', cursor: 'pointer' },
  reserveCheckbox: { width: '18px', height: '18px' },
  pickupSelectedBox: {
    padding: '12px 14px',
    background: '#f3f3f3',
    borderRadius: '10px',
    color: '#2f5fd7',
    fontWeight: '700',
    marginBottom: '12px'
  },
  bottomAction: {
    margin: '20px 10px 20px'
  },
  bottomFixed: {
    position: 'fixed',
    bottom: 0,
    left: 0,
    width: '100%',
    height: '58px',
    padding: '5px 15px 0 15px',
    display: 'flex',
    gap: '16px',
    zIndex: 1000,
    background: '#fff',
    borderTop: '0px solid #eee'
  },
  btnBuyMobile: {
    width: '100%',
    background: '#007bff',
    border: 'none',
    color: '#fff',
    borderRadius: '30px',
    height: '50px',
    fontSize: '15px',
    fontWeight: 500,
    cursor: 'pointer'
  },
};

export default OrderSelected;